/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import UAParser from 'ua-parser-js';

const parser = new UAParser();
const isFirefox = () => parser.getEngine().name === 'Gecko';

const isChrome = () => parser.getEngine().name === 'Blink';

const getCookie = (key: string) => {
  const name = `${key}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

const setCookie = (key: string, value: string) => {
  document.cookie = `${key}=${value}; path=/; secure; samesite=lax`;
};

const deleteCookie = (key: string) => {
  document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Reads the 'chr_prefs' cookie, decodes from Base64, then parses as JSON. Returns an object or null if not set/invalid.
 */
export const getChrPrefs = (): Record<string, any> | null => {
  const value = getCookie('chr_prefs');
  if (!value) return null;
  try {
    return JSON.parse(decodeURIComponent(atob(value)));
  } catch {
    return null;
  }
};

/**
 * Sets the 'chr_prefs' cookie with the given object as a Base64-encoded JSON string.
 * @param prefs The preferences object to store
 * @param options Optional cookie attributes (domain, secure, samesite)
 */
export const setChrPrefs = (
  prefs: Record<string, any>,
  options?: {
    domain?: string;
    secure?: boolean;
    sameSite?: 'lax' | 'strict' | 'none';
  }
) => {
  const encoded = btoa(encodeURIComponent(JSON.stringify(prefs)));
  const expiryDate = new Date();
  expiryDate.setFullYear(expiryDate.getFullYear() + 1); // Set expiry to 1 year from now

  let cookie = `chr_prefs=${encoded}; path=/; expires=${expiryDate.toUTCString()}`;
  if (options?.domain) cookie += `; domain=${options.domain}`;
  if (options?.secure) cookie += '; secure';
  if (options?.sameSite) cookie += `; samesite=${options.sameSite}`;
  document.cookie = cookie;
};

export { isChrome, isFirefox, getCookie, setCookie, deleteCookie };
